import { useCallback, useEffect, useState } from 'react';
import { getOpenPlatformToken } from '@/services/bff/open-platform.api';

const REFRESH_ADVANCE_MS = 5 * 60 * 1000;
const RETRY_INTERVAL_MS = 30 * 1000;

interface UseOpenPlatformTokenOptions {
  enabled?: boolean;
  refreshKey?: string;
}

interface OpenPlatformTokenState {
  token?: string;
  tokenError: boolean;
  retry: () => void;
}

export function useOpenPlatformToken({
  enabled = true,
  refreshKey,
}: UseOpenPlatformTokenOptions = {}): OpenPlatformTokenState {
  const [retryCount, setRetryCount] = useState(0);
  const requestKey = JSON.stringify([enabled, refreshKey, retryCount]);
  const [state, setState] = useState<{
    requestKey: string;
    token?: string;
    tokenError: boolean;
  }>();
  const retry = useCallback(() => setRetryCount((count) => count + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState(undefined);
      return;
    }

    let active = true;
    let timer: number | undefined;
    let expiryTimer: number | undefined;
    let tokenExpiresAt = 0;
    setState({ requestKey, tokenError: false });

    const scheduleRefresh = (delay: number) => {
      timer = window.setTimeout(
        () => void loadToken(),
        Math.max(delay, RETRY_INTERVAL_MS),
      );
    };

    const loadToken = async () => {
      try {
        const result = await getOpenPlatformToken();
        if (!active) return;
        if (
          !result.accessToken ||
          !Number.isFinite(result.expiresAt) ||
          result.expiresAt <= Date.now()
        ) {
          throw new Error('开放平台 Token 已过期');
        }

        tokenExpiresAt = result.expiresAt;
        setState({ requestKey, token: result.accessToken, tokenError: false });
        if (expiryTimer !== undefined) window.clearTimeout(expiryTimer);
        expiryTimer = window.setTimeout(() => {
          if (active) setState({ requestKey, tokenError: true });
        }, result.expiresAt - Date.now());
        scheduleRefresh(result.expiresAt - Date.now() - REFRESH_ADVANCE_MS);
      } catch {
        if (!active) return;

        if (!tokenExpiresAt || Date.now() >= tokenExpiresAt) {
          setState({ requestKey, tokenError: true });
        }
        scheduleRefresh(RETRY_INTERVAL_MS);
      }
    };

    void loadToken();

    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
      if (expiryTimer !== undefined) window.clearTimeout(expiryTimer);
    };
  }, [enabled, requestKey]);

  // Hide the previous identity's Token during render, before effect cleanup runs.
  const currentState =
    enabled && state?.requestKey === requestKey ? state : undefined;
  return {
    token: currentState?.token,
    tokenError: currentState?.tokenError ?? false,
    retry,
  };
}
